import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowClockwise,
  ArrowRight,
  CheckCircle,
  CircleNotch,
  ClockCounterClockwise,
  MagnifyingGlass,
  Microphone,
  Plus,
  Sparkle,
  Stop,
  Trash,
  X,
} from '@phosphor-icons/react';

import {
  sendAIChat,
  streamAIChat,
  TOOL_DOMAIN,
  type AIChatResponse,
  type AIChatTurn,
  type AIChatRequest,
} from '@/api/aichat';
import { approveCommand, dismissCommand, type CommandIntent, type OperationEnvelope } from '@/api/commands';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { TranscribeError, transcribeAudio } from '@/api/transcribe';
import {
  listConversations,
  getConversation,
  deleteConversation,
  type ConversationDetail,
} from '@/api/aiconversations';

// Voice input timing knob. RECORDING_CAP_S is the hard ceiling on a
// single recording session; the mic auto-stops at that point so a stuck
// mic doesn't burn forever. After transcription, the text just lands in
// the input — the user reviews + presses Enter / submit themselves, no
// auto-send (deliberately removed: the original auto-send raced with
// manual submits and produced double-fired queries).
const RECORDING_CAP_S = 30;

// Upper bound (px) on how tall the auto-growing message textareas get before
// they start scrolling internally — keeps a long paste from eating the panel.
const MAX_TEXTAREA_HEIGHT = 140;

function pickSupportedMimeType(): string | undefined {
  if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') {
    return undefined;
  }
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ];
  for (const c of candidates) {
    if (window.MediaRecorder.isTypeSupported(c)) return c;
  }
  return undefined;
}

function micUnavailableInBrowser(): boolean {
  if (typeof window === 'undefined') return true;
  if (typeof window.MediaRecorder === 'undefined') return true;
  if (!navigator.mediaDevices?.getUserMedia) return true;
  return false;
}

// AICommandBar is the natural-language command bar / AI chat assistant.
// It ships as a floating launcher (`variant="fab"`) fixed to the bottom-right
// corner, mounted once by DashboardShell so it's available on every org page —
// the "ai chat bubble" pattern. Clicking the circle opens a conversation
// window (anchored bottom-right, opening upward) rendering the AI's reply, the
// tool trail, and any pending CommandIntents the AI proposed (each with
// Approve / Reject buttons). The `header` / `hero` variants keep the original
// inline-pill layout for callers that still want it.
//
// Plan reference: Decision 5. Approval routes through the existing
// /commands/{id}/approve endpoint, which writes core.audit_log + stores
// inverse_ops for the 30-day revert window. We don't add a parallel write
// path — the AI is just another shape of CommandIntent producer.

export interface AICommandBarProps {
  slug: string;
  // Placement variant:
  // - 'header' (default): compact h-10 pill, panel anchored below it. The
  //   original in-header affordance.
  // - 'hero': larger input (~h-16), wider container, starter-prompt chips
  //   below — the Home page's main affordance.
  // - 'fab': a floating circular launcher fixed to the bottom-right corner
  //   (the "ai chat bubble" pattern). Collapsed it's just the circle; open
  //   it renders the conversation as a window anchored bottom-right, opening
  //   upward. This is how the assistant ships app-wide, mounted once in the
  //   DashboardShell — see that component.
  variant?: 'header' | 'hero' | 'fab';
}

interface PanelEntry {
  id: string;
  userMessage: string;
  reply: string;
  proposedIntents: CommandIntent[];
  // SPA-relative path suffix when the AI called navigate. The entry
  // renders a "Take me there" button that, when clicked, routes the user
  // and closes the panel — auto-navigating felt presumptuous (the AI
  // often offers navigation as one option among several in its reply).
  navigation?: string;
  errorCode?: string;
}

// ConversationTab is the client-side shape for a persisted conversation.
// `entries` start empty; they're loaded lazily when the user activates
// a tab. `loaded` prevents re-fetching on repeated activations.
interface ConversationTab {
  id: string;
  title: string;
  entries: PanelEntry[];
  loaded: boolean;
}

function messagesToEntries(detail: ConversationDetail): PanelEntry[] {
  return detail.messages.map((m) => ({
    id: m.id,
    userMessage: m.user_message,
    reply: m.assistant_reply,
    proposedIntents: m.proposed_intents,
    ...(m.navigation_path ? { navigation: m.navigation_path } : {}),
    ...(m.error_code ? { errorCode: m.error_code } : {}),
  }));
}

export function AICommandBar({ slug, variant = 'header' }: AICommandBarProps) {
  const hero = variant === 'hero';
  const fab = variant === 'fab';
  const { t, i18n } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Top input (header / hero) and bottom input (panel footer) are separate
  // so typing in one doesn't bleed into the other. The top input always
  // starts a new conversation; the bottom input continues the active one.
  const [topValue, setTopValue] = useState('');
  const [bottomValue, setBottomValue] = useState('');
  const [open, setOpen] = useState(false);

  // Multi-conversation state. `activeTabId` = null means "new conversation"
  // (nothing persisted yet). `preConvEntries` holds error entries from a
  // failed send before any conversation was created — cleared on next send.
  const [tabs, setTabs] = useState<ConversationTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [preConvEntries, setPreConvEntries] = useState<PanelEntry[]>([]);
  // preview holds the live streamed answer while a send is in flight — the
  // "typing" text plus the tools the agent is using. It's transient UI only,
  // kept separate from the entry list so streaming never mutates persisted
  // turns; onSuccess replaces it with the authoritative entry, onSettled clears
  // it. null when not streaming.
  const [preview, setPreview] = useState<{ text: string; tools: string[] } | null>(null);
  const [loadingTabId, setLoadingTabId] = useState<string | null>(null);
  // Conversation IDs the user has dismissed locally. If the server-side
  // DELETE 404s (stale row, mismatched user_id after re-auth, slug recycled
  // to a new org…), the row keeps appearing in listConversations and the
  // merge effect below would re-add it as a tab. This set keeps it hidden
  // so the tab actually goes away from the user's point of view even when
  // the server didn't cooperate. Declared up here so the merge useEffect
  // can reference it before handleDeleteTab is defined.
  const [hiddenTabIds, setHiddenTabIds] = useState<Set<string>>(new Set());

  // History fold-out panel toggle + search query.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySearch, setHistorySearch] = useState('');

  // Mobile (<640px) detection drives the panel-positioning fork below. The
  // header instance of AICommandBar sits between the hamburger and the
  // notifications/settings icons on phones, so the input pill is narrow
  // and a panel anchored `absolute left-0 right-0` to the wrapper inherits
  // that narrow width. On mobile we switch the header panel to viewport-
  // anchored `fixed` positioning with small side margins; tablets/desktop
  // and the hero (Home page) instance keep the original absolute layout.
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(max-width: 639px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const bottomInputRef = useRef<HTMLTextAreaElement>(null);

  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [micUnsupported, setMicUnsupported] = useState<boolean>(() => micUnavailableInBrowser());
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingElapsedRef = useRef(0);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // voiceDraftRef tracks whether the current draft has been touched by the
  // voice-input path. Set when transcription appends text; cleared after a
  // successful submit. Lets us forward an honest voice_used flag to the
  // backend's ai.usage_log row.
  const voiceDraftRef = useRef(false);

  // Derived: entries for the currently visible panel.
  const activeTab = tabs.find((t) => t.id === activeTabId) ?? null;
  const activeEntries = activeTabId ? (activeTab?.entries ?? []) : preConvEntries;

  // Load the user's saved conversations on mount so tabs populate from
  // previous sessions.
  const { data: savedConversations } = useQuery({
    queryKey: ['ai-conversations', slug],
    queryFn: () => listConversations(token, slug),
    staleTime: 5 * 60 * 1000,
    enabled: !!token,
  });

  useEffect(() => {
    if (!savedConversations) return;
    setTabs((prev) => {
      const existingIds = new Set(prev.map((t) => t.id));
      const incoming = savedConversations
        .filter((c) => !existingIds.has(c.id))
        .filter((c) => !hiddenTabIds.has(c.id))
        .map((c) => ({ id: c.id, title: c.title, entries: [], loaded: false }));
      return [...prev, ...incoming];
    });
  }, [savedConversations, hiddenTabIds]);

  // Click-outside + Escape close the panel.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (wrapperRef.current?.contains(target)) return;
      // Ignore clicks that land inside a modal layer (Radix dialogs render
      // outside this panel's wrapper, e.g. the location-import dialog, the
      // location-form modal, any confirm raised from an approval flow).
      // Without this guard, clicking Confirm / Cancel on those dialogs
      // triggers setOpen(false) here and the panel collapses mid-flow.
      if (target instanceof Element && target.closest('[role="dialog"]')) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Mobile-only: measure the input pill's viewport-bottom and use it as
  // the panel's `top`. The sticky header may be offset by an environment
  // banner so a hardcoded value (`top-16` etc.) would be wrong whenever
  // the dev/staging banner is visible. We re-measure on resize and on
  // scroll so the panel tracks the sticky header.
  const [mobileTop, setMobileTop] = useState<number>(60);
  useLayoutEffect(() => {
    if (!open || !isMobile || hero || fab) return;
    const update = () => {
      if (!inputRef.current) return;
      setMobileTop(inputRef.current.getBoundingClientRect().bottom + 8);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, isMobile, hero, fab]);


  // Auto-grow the textareas as the user types multiple lines, capped so a
  // long paste doesn't swallow the panel. Runs on every value change so it
  // also handles programmatic sets (voice transcription) and resets back to
  // one row after a submit clears the field.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
  }, [topValue, hero]);
  useLayoutEffect(() => {
    const el = bottomInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
  }, [bottomValue]);

  // Enter submits the form; Shift+Enter inserts a newline (standard chat
  // behaviour). The isComposing guard keeps an IME confirmation Enter from
  // firing a send mid-composition.
  const submitOnEnter = (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      e.currentTarget.form?.requestSubmit();
    }
  };

  // Update entries for the active conversation (handles both tabbed and
  // pre-conversation state uniformly).
  const updateActiveEntries = (transform: (entries: PanelEntry[]) => PanelEntry[]) => {
    if (activeTabId) {
      setTabs((prev) =>
        prev.map((t) => (t.id === activeTabId ? { ...t, entries: transform(t.entries) } : t)),
      );
    } else {
      setPreConvEntries((prev) => transform(prev));
    }
  };

  // `forceNew=true` is used by the top input — it always starts a fresh
  // conversation regardless of which tab is active. `forceNew=false` (bottom
  // input) appends to the current tab.
  const send = useMutation({
    mutationFn: async ({
      message,
      forceNew,
    }: {
      message: string;
      forceNew: boolean;
    }): Promise<AIChatResponse> => {
      const trimmed = message.trim();
      if (!trimmed) throw new Error('empty');
      const convId = forceNew ? undefined : (activeTabId ?? undefined);
      const turns: AIChatTurn[] = (forceNew ? [] : activeEntries).flatMap((h) => [
        { role: 'user' as const, text: h.userMessage },
        { role: 'assistant' as const, text: h.reply },
      ]);
      const language = i18n.language?.split('-')[0];
      const body: AIChatRequest = {
        message: trimmed,
        history: turns,
        voice_used: voiceDraftRef.current,
        ...(language ? { language } : {}),
        ...(convId ? { conversation_id: convId } : {}),
      };

      // Reset the live preview and stream. On any streaming-transport failure
      // (proxy buffering, no Flusher, network) fall back to the one-shot POST so
      // the user still gets a reply — genuine input/rate/AI errors are rethrown
      // so onError surfaces them rather than silently retrying.
      setPreview({ text: '', tools: [] });
      try {
        return await streamAIChat(token, slug, body, {
          onText: (delta) => setPreview((p) => (p ? { ...p, text: p.text + delta } : p)),
          onTool: (name) =>
            setPreview((p) => (p && !p.tools.includes(name) ? { ...p, tools: [...p.tools, name] } : p)),
        });
      } catch (err) {
        const code = err instanceof ApiError ? err.code : '';
        if (['invalid_input', 'rate_limited', 'ai_disabled', 'ai_loop_exceeded'].includes(code)) {
          throw err;
        }
        return sendAIChat(token, slug, body);
      }
    },
    onSuccess: (res, { message, forceNew }) => {
      const newEntry: PanelEntry = {
        id: crypto.randomUUID(),
        userMessage: message,
        reply: res.reply,
        proposedIntents: res.proposed_intents,
        ...(res.navigation ? { navigation: res.navigation } : {}),
      };
      const convId = res.conversation_id;

      if (forceNew || !activeTabId) {
        // Server created a new conversation — establish a tab for it.
        const title = message.length > 40 ? `${message.slice(0, 40)}…` : message;
        setTabs((prev) => [{ id: convId, title, entries: [newEntry], loaded: true }, ...prev]);
        setActiveTabId(convId);
        setPreConvEntries([]);
      } else {
        setTabs((prev) =>
          prev.map((t) =>
            t.id === activeTabId ? { ...t, entries: [...t.entries, newEntry] } : t,
          ),
        );
      }

      voiceDraftRef.current = false;
      void queryClient.invalidateQueries({ queryKey: ['ai-conversations', slug] });
      requestAnimationFrame(() => bottomInputRef.current?.focus());
    },
    onError: (err, { message, forceNew }) => {
      const code = err instanceof ApiError ? err.code : 'unknown';
      const errorEntry: PanelEntry = {
        id: crypto.randomUUID(),
        userMessage: message,
        reply: '',
        proposedIntents: [],
        errorCode: code,
      };
      if (forceNew || !activeTabId) {
        setPreConvEntries([errorEntry]); // replace the optimistic placeholder
      } else {
        updateActiveEntries((prev) => [...prev, errorEntry]);
      }
    },
    // Clear the live preview once the turn resolves — the authoritative entry
    // (onSuccess) or error entry (onError) has taken over by now.
    onSettled: () => setPreview(null),
  });

  // Top input submit — always starts a new conversation.
  // Optimistically switch to the new-conversation context immediately so the
  // "thinking" indicator appears in the fresh tab, not in whichever tab was active.
  const onSubmitTop = (e: FormEvent) => {
    e.preventDefault();
    if (!topValue.trim() || send.isPending) return;
    const message = topValue.trim();
    setTopValue('');
    setOpen(true);
    setActiveTabId(null);
    setPreConvEntries([{ id: crypto.randomUUID(), userMessage: message, reply: '', proposedIntents: [] }]);
    send.mutate({ message, forceNew: true });
  };

  // External submit hook. Settings-page buttons (e.g. "Suggest team
  // categories with AI") dispatch CustomEvent('ai-command-bar:submit',
  // { detail: { prompt } }) to start a fresh conversation seeded with a
  // specific instruction — same code path as onSubmitTop so we get the
  // optimistic pre-conv entry + the standard panel-open behaviour for
  // free. forceNew is always true; the caller's prompt is the seed, not
  // a follow-up.
  useEffect(() => {
    const onExternalSubmit = (e: Event) => {
      const detail = (e as CustomEvent<{ prompt?: string }>).detail;
      const message = detail?.prompt?.trim();
      if (!message || send.isPending) return;
      setTopValue('');
      setOpen(true);
      setActiveTabId(null);
      setPreConvEntries([
        { id: crypto.randomUUID(), userMessage: message, reply: '', proposedIntents: [] },
      ]);
      send.mutate({ message, forceNew: true });
    };
    window.addEventListener('ai-command-bar:submit', onExternalSubmit);
    return () => window.removeEventListener('ai-command-bar:submit', onExternalSubmit);
  }, [send]);

  // Re-send the user message from a failed entry. Drops the error entry first
  // (so the failed turn isn't carried into the history payload) and uses the
  // same forceNew/append rules as the original submit paths.
  const retry = (entryId: string, message: string) => {
    if (send.isPending || !message.trim()) return;
    if (activeTabId) {
      setTabs((prev) =>
        prev.map((tab) =>
          tab.id === activeTabId
            ? { ...tab, entries: tab.entries.filter((e) => e.id !== entryId) }
            : tab,
        ),
      );
      send.mutate({ message, forceNew: false });
    } else {
      setPreConvEntries([
        { id: crypto.randomUUID(), userMessage: message, reply: '', proposedIntents: [] },
      ]);
      send.mutate({ message, forceNew: true });
    }
  };

  // Bottom input submit — continues the active conversation.
  const onSubmitBottom = (e: FormEvent) => {
    e.preventDefault();
    if (!bottomValue.trim() || send.isPending) return;
    const message = bottomValue.trim();
    setBottomValue('');
    send.mutate({ message, forceNew: false });
  };

  // ---- Voice: mic recording + transcription ----

  function cleanupRecording() {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    mediaRecorderRef.current = null;
    recordingElapsedRef.current = 0;
    setRecording(false);
  }

  useEffect(() => {
    return () => {
      cleanupRecording();
    };
  }, []);

  async function startRecording() {
    if (recording || transcribing || micUnsupported || send.isPending) return;
    setVoiceError(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = err instanceof Error ? err.name : '';
      setVoiceError(
        name === 'NotAllowedError' || name === 'SecurityError' ? 'permission' : 'unavailable',
      );
      return;
    }
    mediaStreamRef.current = stream;
    const mimeType = pickSupportedMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
      setMicUnsupported(true);
      return;
    }
    audioChunksRef.current = [];
    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) audioChunksRef.current.push(ev.data);
    };
    recorder.onerror = () => {
      cleanupRecording();
      setVoiceError('failed');
    };
    recorder.onstop = () => {
      void onRecordingStopped(recorder.mimeType || mimeType || 'audio/webm');
    };
    recorder.start();
    mediaRecorderRef.current = recorder;
    setRecording(true);
    recordingElapsedRef.current = 0;
    recordingTimerRef.current = setInterval(() => {
      recordingElapsedRef.current += 1;
      if (recordingElapsedRef.current >= RECORDING_CAP_S) {
        mediaRecorderRef.current?.stop();
      }
    }, 1000);
  }

  function stopRecording() {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;
    if (recorder.state !== 'inactive') recorder.stop();
  }

  async function onRecordingStopped(mimeType: string) {
    const chunks = audioChunksRef.current;
    audioChunksRef.current = [];
    cleanupRecording();
    if (chunks.length === 0) {
      setVoiceError('empty');
      return;
    }
    const blob = new Blob(chunks, { type: mimeType });
    if (blob.size === 0) {
      setVoiceError('empty');
      return;
    }
    setTranscribing(true);
    try {
      const language = i18n.language?.split('-')[0] ?? '';
      const text = await transcribeAudio(token, slug, blob, language);
      // Voice transcription always goes into the top input since the mic
      // button lives in the top bar. The user can then submit from there
      // (which creates a new conversation) or copy the text manually.
      const combined = topValue.trim() ? `${topValue.trim()} ${text}` : text;
      setTopValue(combined);
      voiceDraftRef.current = true;
      requestAnimationFrame(() => inputRef.current?.focus());
    } catch (err) {
      if (err instanceof TranscribeError) {
        if (err.code === 'transcribe_unavailable') {
          setMicUnsupported(true);
          setVoiceError(null);
        } else if (err.code === 'empty_audio') {
          setVoiceError('empty');
        } else if (err.code === 'too_large') {
          setVoiceError('tooLong');
        } else {
          setVoiceError('failed');
        }
      } else {
        setVoiceError('failed');
      }
    } finally {
      setTranscribing(false);
    }
  }

  // ---- Intent callbacks ----

  // Keep the conversation entry visible after approval / rejection so the
  // transcript records what happened — the original behaviour filtered
  // the entry out and the user lost the context of what they just did
  // (and reopening the conversation from history showed only a tiny
  // grey "Approved" line that was easy to miss). The ProposedIntent
  // component below renders a tinted-box badge for executed / rejected
  // / failed states so the outcome reads at a glance.
  const onIntentResolved = (entryId: string, intentId: string, next: CommandIntent) => {
    updateActiveEntries((entries) =>
      entries.map((e) =>
        e.id !== entryId
          ? e
          : {
              ...e,
              proposedIntents: e.proposedIntents.map((i) => (i.id === intentId ? next : i)),
            },
      ),
    );
    void queryClient.invalidateQueries();
  };

  const onIntentRejected = (entryId: string, intentId: string) => {
    updateActiveEntries((entries) =>
      entries.map((e) =>
        e.id !== entryId
          ? e
          : {
              ...e,
              proposedIntents: e.proposedIntents.map((i) =>
                i.id === intentId ? { ...i, status: 'rejected' as const } : i,
              ),
            },
      ),
    );
    void dismissCommand(token, slug, intentId).catch(() => {});
  };

  // ---- Tab management ----

  const activateTab = async (tabId: string) => {
    setActiveTabId(tabId);
    setHistoryOpen(false);
    setOpen(true);

    let tab = tabs.find((t) => t.id === tabId);

    // Tab exists on the server but hasn't been merged into local state yet — add it.
    if (!tab) {
      const serverConv = savedConversations?.find((c) => c.id === tabId);
      if (!serverConv) return;
      const stub: ConversationTab = { id: serverConv.id, title: serverConv.title, entries: [], loaded: false };
      setTabs((prev) => [...prev, stub]);
      tab = stub;
    }

    if (tab.loaded) return;
    setLoadingTabId(tabId);
    try {
      const detail = await queryClient.fetchQuery<ConversationDetail>({
        queryKey: ['ai-conversation', slug, tabId],
        queryFn: () => getConversation(token, slug, tabId),
      });
      setTabs((prev) =>
        prev.map((t) =>
          t.id === tabId ? { ...t, loaded: true, entries: messagesToEntries(detail) } : t,
        ),
      );
    } catch {
      // Leave loaded = false so the user can retry.
    } finally {
      setLoadingTabId(null);
    }
  };

  // Deleting a conversation is immediate — no confirmation step. A thread is
  // cheap and re-askable, so the extra click was pure friction.
  //
  // Order matters: the list query is invalidated only AFTER the DELETE settles.
  // Firing both together let the refetch win the race and come back with the
  // row still present, and since the tab strip re-derives from that query
  // result, the tab reappeared and the delete looked like a no-op.
  const handleDeleteTab = async (tabId: string) => {
    setHiddenTabIds((prev) => {
      const next = new Set(prev);
      next.add(tabId);
      return next;
    });
    setTabs((prev) => prev.filter((t) => t.id !== tabId));
    if (activeTabId === tabId) {
      setActiveTabId(null);
      setPreConvEntries([]);
    }
    try {
      await deleteConversation(token, slug, tabId);
    } catch {
      // hiddenTabIds is the user-visible source of truth, so the thread stays
      // gone from this session either way. Nothing actionable to surface for a
      // failed server-side cleanup.
    }
    void queryClient.invalidateQueries({ queryKey: ['ai-conversations', slug] });
  };

  const openNewConversation = () => {
    setActiveTabId(null);
    setPreConvEntries([]);
    setBottomValue('');
    setOpen(true);
    setHistoryOpen(false);
    requestAnimationFrame(() => bottomInputRef.current?.focus());
  };

  // Panel visibility: keep open whenever the user has opened it — don't auto-close
  // when tabs/entries empty out (e.g. after deleting the last tab).
  const showPanel = open;

  // Compute visibleTabs by merging local tabs state (has loaded entries, includes
  // just-created conversations) with savedConversations (server truth).  Using
  // savedConversations directly avoids a race where the useEffect hasn't had a
  // chance to merge server data into tabs state yet.
  const tabsById = new Map(tabs.map((t) => [t.id, t]));
  const serverIds = new Set((savedConversations ?? []).map((c) => c.id));
  const localNew = tabs.filter((t) => !serverIds.has(t.id));
  // …but because it reads the query result directly it MUST also apply
  // hiddenTabIds — otherwise a just-deleted conversation renders straight back
  // out of the cached list and the delete looks like it did nothing.
  const allTabs: ConversationTab[] = [
    ...localNew,
    ...(savedConversations ?? []).map(
      (c) => tabsById.get(c.id) ?? { id: c.id, title: c.title, entries: [], loaded: false },
    ),
  ].filter((t) => !hiddenTabIds.has(t.id));
  const visibleTabs = allTabs.slice(0, 3);

  // Starter prompts — shown in the hero input and in the empty fab window,
  // hidden once any conversation exists.
  const suggestions =
    (hero || fab) && activeEntries.length === 0 && preConvEntries.length === 0
      ? (t('aiBar.suggestions', { returnObjects: true, defaultValue: [] }) as string[])
      : [];

  // History panel: filtered list from the server query.
  const historyList = (savedConversations ?? [])
    .filter((c) => !hiddenTabIds.has(c.id))
    .filter((c) =>
      historySearch.trim()
        ? c.title.toLowerCase().includes(historySearch.toLowerCase())
        : true,
    );

  // Mic button — shared between the inline top row (header / hero) and the
  // fab window footer. `hero` drives the larger sizing; in fab mode hero is
  // false so it renders at the compact size the footer is built for.
  const micButton = !micUnsupported ? (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        if (recording) stopRecording();
        else void startRecording();
      }}
      onContextMenu={(e) => e.preventDefault()}
      disabled={transcribing || send.isPending}
      aria-label={
        recording
          ? t('aiBar.voice.stop')
          : transcribing
            ? t('aiBar.voice.transcribing')
            : t('aiBar.voice.start')
      }
      aria-pressed={recording}
      title={t('aiBar.voice.tooltip')}
      className={
        (hero
          ? 'flex h-11 w-11 shrink-0 items-center justify-center rounded-pill bg-[var(--lime,#BEE034)] text-foreground shadow-sm transition-[transform,box-shadow] '
          : 'flex h-7 w-7 shrink-0 items-center justify-center rounded-pill bg-[var(--lime,#BEE034)] text-foreground transition-[transform,box-shadow] ') +
        (recording
          ? 'ring-4 ring-[var(--lime-light,#D4F56A)] motion-safe:animate-pulse '
          : 'hover:scale-105 motion-reduce:hover:scale-100 ') +
        'select-none touch-none disabled:opacity-50 disabled:hover:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      }
    >
      {transcribing ? (
        <CircleNotch
          className={
            hero
              ? 'h-5 w-5 animate-spin motion-reduce:animate-none'
              : 'h-3.5 w-3.5 animate-spin motion-reduce:animate-none'
          }
          weight="bold"
          aria-hidden
        />
      ) : recording ? (
        <Stop className={hero ? 'h-5 w-5' : 'h-3.5 w-3.5'} weight="fill" aria-hidden />
      ) : (
        <Microphone className={hero ? 'h-5 w-5' : 'h-3.5 w-3.5'} weight="bold" aria-hidden />
      )}
    </button>
  ) : null;

  return (
    <div
      ref={wrapperRef}
      className={
        fab
          ? 'fixed bottom-4 right-4 z-50'
          : hero
            ? 'relative w-full max-w-3xl'
            : 'relative w-full max-w-2xl'
      }
    >
      {/* Floating launcher — fab variant only. Collapsed, this circle is the
          only visible chrome; clicking it toggles the conversation window,
          which opens upward from just above the circle. */}
      {fab && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? t('aiBar.close') : t('aiBar.launcher')}
          aria-haspopup="dialog"
          aria-expanded={open}
          className="ml-auto flex h-14 w-14 items-center justify-center rounded-pill bg-[var(--lime,#BEE034)] text-foreground shadow-lg transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--lime,#BEE034)] focus-visible:ring-offset-2 focus-visible:ring-offset-background motion-reduce:hover:scale-100"
        >
          {open ? (
            <X className="h-6 w-6" weight="bold" aria-hidden />
          ) : (
            <Sparkle className="h-6 w-6" weight="fill" aria-hidden />
          )}
        </button>
      )}

      {!fab && (
      <>
      <div className="flex items-center gap-2">
        {/* Top input — always starts a new conversation on submit */}
        <form onSubmit={onSubmitTop} className="relative flex-1">
          <Sparkle
            weight="fill"
            aria-hidden
            className={
              hero
                ? 'pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--lime,#BEE034)]'
                : 'pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--lime,#BEE034)]'
            }
          />
          <textarea
            ref={inputRef}
            rows={1}
            value={topValue}
            onChange={(e) => setTopValue(e.target.value)}
            onKeyDown={submitOnEnter}
            onFocus={() => {
              // Reopen the panel whenever the input gains focus AND there's
              // something to show — an existing tab, in-flight pre-conv
              // entries, or a request still resolving. Previously this only
              // checked tabs.length > 0, which meant clicking the input
              // after closing the panel mid-prompt left it closed even
              // though the AI was still working in the background.
              if (
                tabs.length > 0 ||
                preConvEntries.length > 0 ||
                send.isPending
              ) {
                setOpen(true);
              }
            }}
            placeholder={t('aiBar.placeholder')}
            aria-label={t('aiBar.ariaLabel')}
            maxLength={4096}
            // readOnly (not disabled) while a request is in flight: keeps
            // the input from being edited but still receives focus/click
            // events so the user can re-open the panel by tapping the bar
            // — the previous disabled state swallowed those events, so
            // closing the panel mid-prompt stranded the user.
            readOnly={send.isPending}
            className={
              hero
                ? 'block min-h-16 w-full resize-none overflow-y-auto rounded-3xl border border-border bg-background py-5 pl-14 pr-16 text-base font-medium leading-6 text-foreground shadow-sm placeholder:text-muted-foreground/70 transition-shadow focus-visible:border-[var(--lime,#BEE034)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--lime,#BEE034)] read-only:opacity-60'
                : 'block min-h-10 w-full resize-none overflow-y-auto rounded-3xl border border-border bg-background py-2.5 pl-11 pr-12 text-sm font-medium leading-5 text-foreground placeholder:text-muted-foreground/80 transition-shadow focus-visible:border-[var(--lime,#BEE034)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--lime,#BEE034)] read-only:opacity-60'
            }
          />
          <button
            type="submit"
            disabled={!topValue.trim() || send.isPending}
            aria-label={send.isPending ? t('aiBar.thinking') : t('aiBar.submit')}
            className={
              hero
                ? 'absolute right-2.5 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-pill bg-foreground text-background transition-transform hover:scale-105 disabled:opacity-40 disabled:hover:scale-100 motion-reduce:hover:scale-100'
                : 'absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-pill bg-foreground text-background transition-transform hover:scale-105 disabled:opacity-40 disabled:hover:scale-100 motion-reduce:hover:scale-100'
            }
          >
            {send.isPending ? (
              <CircleNotch
                className={
                  hero
                    ? 'h-5 w-5 animate-spin motion-reduce:animate-none'
                    : 'h-3.5 w-3.5 animate-spin motion-reduce:animate-none'
                }
                weight="bold"
                aria-hidden
              />
            ) : (
              <ArrowRight className={hero ? 'h-5 w-5' : 'h-3.5 w-3.5'} weight="bold" aria-hidden />
            )}
          </button>
        </form>

        {/* Mic button */}
        {micButton}
      </div>

      {/* Inline voice error */}
      {voiceError && !recording && !transcribing && (
        <p role="alert" className="mt-2 px-2 text-xs text-destructive">
          {t(`aiBar.voice.error.${voiceError}`, { defaultValue: t('aiBar.voice.error.failed') })}
        </p>
      )}

      {/* Starter-prompt chips */}
      {Array.isArray(suggestions) && suggestions.length > 0 && (
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setTopValue(s);
                inputRef.current?.focus();
              }}
              className="rounded-pill border border-border bg-card px-3.5 py-1.5 text-xs font-semibold text-foreground/80 transition-colors hover:border-foreground/30 hover:bg-muted hover:text-foreground"
            >
              {s}
            </button>
          ))}
        </div>
      )}
      </>
      )}

      {showPanel && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label={t('aiBar.panelTitle')}
          className={
            fab
              ? isMobile
                ? 'fixed inset-x-2 bottom-[5.5rem] top-4 z-50 flex flex-col rounded-2xl border border-border bg-background shadow-2xl'
                : 'absolute bottom-full right-0 z-50 mb-3 flex w-[380px] max-w-[calc(100vw-2rem)] flex-col rounded-2xl border border-border bg-background shadow-2xl'
              : isMobile && !hero
                ? 'fixed left-2 right-2 z-40 flex flex-col rounded-2xl border border-border bg-background shadow-xl'
                : 'absolute left-0 right-0 top-full z-40 mt-2 flex flex-col rounded-2xl border border-border bg-background shadow-xl'
          }
          style={
            fab
              ? isMobile
                ? undefined
                : { height: 'min(600px, calc(100dvh - 7rem))' }
              : isMobile && !hero
                ? { top: `${mobileTop}px`, maxHeight: `calc(100dvh - ${mobileTop}px - 12px)` }
                : { maxHeight: '70vh' }
          }
        >
          {/* Panel header: tab strip + title bar */}
          <header className="shrink-0 border-b border-border">
            {/* Tab strip: 3 most-recent tabs, "+" at the right end, count at far right */}
            <div className="flex items-center gap-0.5 overflow-x-auto px-2 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {visibleTabs.map((tab) => (
                <div
                  key={tab.id}
                  className={
                    'group flex h-7 max-w-[160px] shrink-0 items-center rounded-t-lg text-xs font-semibold transition-colors ' +
                    (activeTabId === tab.id
                      ? 'border-b-2 border-[var(--lime,#BEE034)] bg-muted text-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground')
                  }
                >
                  <button
                    type="button"
                    onClick={() => void activateTab(tab.id)}
                    title={tab.title || t('aiBar.conversationN')}
                    className="flex h-full min-w-0 flex-1 items-center gap-1.5 pl-3 pr-1"
                  >
                    {loadingTabId === tab.id ? (
                      <CircleNotch className="h-3 w-3 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden />
                    ) : null}
                    <span className="truncate">{tab.title || t('aiBar.conversationN')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDeleteTab(tab.id)}
                    aria-label={t('aiBar.deleteConversation.title')}
                    title={t('aiBar.deleteConversation.title')}
                    className="mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded opacity-50 transition-opacity hover:bg-foreground/10 hover:opacity-100 focus-visible:opacity-100"
                  >
                    <X className="h-3 w-3" weight="bold" />
                  </button>
                </div>
              ))}

              {/* New conversation — right-aligned at the end of the strip */}
              <button
                type="button"
                onClick={openNewConversation}
                aria-label={t('aiBar.newConversation')}
                title={t('aiBar.newConversation')}
                className={
                  'ml-1 flex h-7 shrink-0 items-center gap-1 rounded-t-lg px-2.5 text-xs font-semibold transition-colors ' +
                  (activeTabId === null
                    ? 'border-b-2 border-[var(--lime,#BEE034)] bg-muted text-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground')
                }
              >
                <Plus className="h-3 w-3" weight="bold" aria-hidden />
                {t('aiBar.newConversation')}
              </button>

              {/* Tab counter — only shown when there are more tabs than visible */}
              {allTabs.length > visibleTabs.length && (
                <span className="ml-auto shrink-0 pr-1 text-xs tabular-nums text-muted-foreground">
                  {visibleTabs.length}/{allTabs.length}
                </span>
              )}
            </div>

            {/* Title bar: title on left, action icons on right */}
            <div className="flex items-center justify-between gap-2 px-4 py-2">
              <div className="flex items-center gap-2">
                <Sparkle weight="fill" className="h-4 w-4 text-[var(--lime,#BEE034)]" />
                <h2 className="font-heading text-sm font-bold">
                  {activeTab?.title || t('aiBar.panelTitle')}
                </h2>
              </div>
              <div className="flex items-center gap-1">
                {/* History icon — opens the conversation history fold-out */}
                <button
                  type="button"
                  onClick={() => setHistoryOpen((v) => !v)}
                  aria-label={t('aiBar.history')}
                  title={t('aiBar.history')}
                  aria-pressed={historyOpen}
                  className={
                    'rounded-pill p-1.5 transition-colors ' +
                    (historyOpen
                      ? 'bg-muted text-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground')
                  }
                >
                  <ClockCounterClockwise className="h-4 w-4" />
                </button>

                {/* Delete active conversation — only shown when a tab is active */}
                {activeTabId && (
                  <button
                    type="button"
                    onClick={() => void handleDeleteTab(activeTabId)}
                    aria-label={t('aiBar.deleteConversation.title')}
                    title={t('aiBar.deleteConversation.title')}
                    className="rounded-pill p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash className="h-4 w-4" />
                  </button>
                )}

                {/* Close panel */}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label={t('aiBar.close')}
                  className="rounded-pill p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </header>

          {/* Main body — toggles between history fold-out and message list */}
          <div className="flex-1 overflow-y-auto">
            {historyOpen ? (
              /* History panel */
              <div className="px-3 py-3">
                <div className="relative mb-3">
                  <MagnifyingGlass
                    className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                  />
                  <input
                    type="search"
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                    placeholder={t('aiBar.historySearch')}
                    className={`h-8 w-full rounded-pill border border-border bg-background pl-8 ${historySearch ? 'pr-8' : 'pr-3'} text-xs text-foreground placeholder:text-muted-foreground/70 focus-visible:border-[var(--lime,#BEE034)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--lime,#BEE034)]`}
                  />
                  {historySearch.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setHistorySearch('')}
                      aria-label={t('common.clearSearch')}
                      className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <X weight="bold" className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {historyList.length === 0 ? (
                  <p className="py-6 text-center text-xs text-muted-foreground">
                    {historySearch ? t('aiBar.historyNoResults') : t('aiBar.historyEmpty')}
                  </p>
                ) : (
                  <ul className="space-y-1">
                    {historyList.map((c) => (
                      <li
                        key={c.id}
                        className="group flex items-center gap-1 rounded-xl transition-colors hover:bg-muted"
                      >
                        <button
                          type="button"
                          onClick={() => void activateTab(c.id)}
                          className="min-w-0 flex-1 px-3 py-2 text-left text-xs"
                        >
                          <span className="block truncate font-semibold text-foreground">
                            {c.title || t('aiBar.conversationN')}
                          </span>
                          <span className="text-muted-foreground">
                            {new Date(c.updated_at).toLocaleDateString(
                              i18n.resolvedLanguage ?? 'en',
                              { day: 'numeric', month: 'short' },
                            )}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDeleteTab(c.id)}
                          aria-label={t('aiBar.deleteConversation.title')}
                          title={t('aiBar.deleteConversation.title')}
                          className="mr-1.5 shrink-0 rounded-lg p-1.5 text-muted-foreground opacity-0 transition-[opacity,colors] group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive focus-visible:opacity-100"
                        >
                          <Trash className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : fab && activeEntries.length === 0 && !send.isPending ? (
              /* Empty fab window — greeting + starter prompts */
              <div className="flex flex-col items-center gap-4 px-5 py-8 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-pill bg-[var(--lime,#BEE034)] text-foreground">
                  <Sparkle className="h-6 w-6" weight="fill" aria-hidden />
                </span>
                <p className="text-sm text-muted-foreground">{t('aiBar.emptyPrompt')}</p>
                {Array.isArray(suggestions) && suggestions.length > 0 && (
                  <div className="flex flex-wrap justify-center gap-2">
                    {suggestions.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => {
                          setBottomValue(s);
                          bottomInputRef.current?.focus();
                        }}
                        className="rounded-pill border border-border bg-card px-3.5 py-1.5 text-xs font-semibold text-foreground/80 transition-colors hover:border-foreground/30 hover:bg-muted hover:text-foreground"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              /* Message list */
              <div className="divide-y divide-border">
                {activeEntries.map((entry) => (
                  <ConversationEntry
                    key={entry.id}
                    entry={entry}
                    slug={slug}
                    token={token}
                    onIntentResolved={(intentId, next) =>
                      onIntentResolved(entry.id, intentId, next)
                    }
                    onIntentRejected={(intentId) => onIntentRejected(entry.id, intentId)}
                    onNavigate={(path) => {
                      navigate(`/orgs/${slug}${path}`);
                      setOpen(false);
                    }}
                    onRetry={() => retry(entry.id, entry.userMessage)}
                    retryDisabled={send.isPending}
                  />
                ))}
                {send.isPending && (
                  // Live turn: the tools the agent is touching, the streamed
                  // "typing" answer, and — always — an animated working
                  // indicator so a silent stretch (e.g. while the agent runs a
                  // tool after saying "let me collect some information") never
                  // reads as stuck. Replaced by the authoritative entry once
                  // the turn resolves (onSuccess) and cleared onSettled.
                  <div className="flex flex-col gap-2 px-4 py-4" aria-live="polite">
                    {(() => {
                      // Collapse the raw tool ids to deduped domain nouns
                      // ("medlemmar, lag, betalningar"); skip pure-action tools
                      // (propose/navigate) that aren't in the map.
                      const domains = [
                        ...new Set(
                          (preview?.tools ?? [])
                            .map((n) => TOOL_DOMAIN[n])
                            .filter((d): d is string => Boolean(d)),
                        ),
                      ];
                      if (domains.length === 0) return null;
                      return (
                        <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                          <Sparkle
                            weight="fill"
                            aria-hidden
                            className="h-3 w-3 text-[var(--lime,#BEE034)]"
                          />
                          {t('aiBar.lookingAt')}{' '}
                          {domains
                            .map((d) => t(`aiBar.toolDomain.${d}`, { defaultValue: d }))
                            .join(', ')}
                        </p>
                      );
                    })()}
                    {preview?.text && (
                      <div className="flex justify-start">
                        <p className="max-w-[90%] whitespace-pre-wrap rounded-2xl rounded-bl-sm bg-muted px-3 py-2 text-sm text-foreground">
                          {preview.text}
                        </p>
                      </div>
                    )}
                    <ThinkingDots label={t('aiBar.thinking').replace(/[….]+$/u, '')} />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sticky bottom input — continues the active conversation */}
          <footer className="shrink-0 border-t border-border bg-background px-3 py-2">
            {/* Voice error — fab mode surfaces it here since it has no inline
                top row; header/hero render it above the input instead. */}
            {fab && voiceError && !recording && !transcribing && (
              <p role="alert" className="mb-2 px-1 text-xs text-destructive">
                {t(`aiBar.voice.error.${voiceError}`, {
                  defaultValue: t('aiBar.voice.error.failed'),
                })}
              </p>
            )}
            <form onSubmit={onSubmitBottom} className="relative flex items-center gap-2">
              <div className="relative flex-1">
                <Sparkle
                  weight="fill"
                  aria-hidden
                  className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--lime,#BEE034)]"
                />
                <textarea
                  ref={bottomInputRef}
                  rows={1}
                  value={bottomValue}
                  onChange={(e) => setBottomValue(e.target.value)}
                  onKeyDown={submitOnEnter}
                  placeholder={t('aiBar.replyPlaceholder')}
                  aria-label={t('aiBar.ariaLabel')}
                  maxLength={4096}
                  disabled={send.isPending}
                  className="block min-h-9 w-full resize-none overflow-y-auto rounded-3xl border border-border bg-background py-2 pl-9 pr-10 text-sm font-medium leading-5 text-foreground placeholder:text-muted-foreground/70 transition-shadow focus-visible:border-[var(--lime,#BEE034)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--lime,#BEE034)] disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={!bottomValue.trim() || send.isPending}
                  aria-label={send.isPending ? t('aiBar.thinking') : t('aiBar.submit')}
                  className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-pill bg-foreground text-background transition-transform hover:scale-105 disabled:opacity-40 disabled:hover:scale-100 motion-reduce:hover:scale-100"
                >
                  {send.isPending ? (
                    <CircleNotch
                      className="h-3 w-3 animate-spin motion-reduce:animate-none"
                      weight="bold"
                      aria-hidden
                    />
                  ) : (
                    <ArrowRight className="h-3 w-3" weight="bold" aria-hidden />
                  )}
                </button>
              </div>
              {fab && micButton}
            </form>
          </footer>
        </div>
      )}
    </div>
  );
}

// ThinkingDots is the "still working" affordance shown for the whole life of
// an in-flight turn — a short label plus three staggered pulsing dots. It sits
// below any streamed text so the user always has a live, animated signal the
// assistant hasn't stalled, even during a silent tool call.
function ThinkingDots({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-sm text-muted-foreground" role="status">
      <span>{label}</span>
      <span className="inline-flex items-baseline gap-0.5" aria-hidden>
        {[0, 200, 400].map((delay) => (
          <span
            key={delay}
            className="inline-block h-1.5 w-1.5 animate-pulse rounded-pill bg-[var(--lime,#BEE034)] motion-reduce:animate-none"
            style={{ animationDelay: `${delay}ms`, animationDuration: '1200ms' }}
          />
        ))}
      </span>
    </div>
  );
}

interface ConversationEntryProps {
  entry: PanelEntry;
  slug: string;
  token: string | null;
  onIntentResolved: (intentId: string, next: CommandIntent) => void;
  onIntentRejected: (intentId: string) => void;
  onNavigate: (path: string) => void;
  onRetry: () => void;
  retryDisabled: boolean;
}

function ConversationEntry({
  entry,
  slug,
  token,
  onIntentResolved,
  onIntentRejected,
  onNavigate,
  onRetry,
  retryDisabled,
}: ConversationEntryProps) {
  const { t } = useTranslation('common');

  return (
    <div className="flex flex-col gap-3 px-4 py-4">
      {/* Your own message — right-aligned bubble, like other chat apps. The
          alignment signals the sender; the label stays for screen readers. */}
      <div className="flex justify-end">
        <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-primary px-3 py-2 text-sm font-medium text-primary-foreground">
          <span className="sr-only">{t('aiBar.youAsked')}: </span>
          {entry.userMessage}
        </p>
      </div>

      {entry.errorCode ? (
        <div className="mt-3 rounded-2xl bg-destructive/10 px-3 py-2">
          <p className="text-sm text-destructive">
            {t(`aiBar.error.${entry.errorCode}`, { defaultValue: t('aiBar.error.unknown') })}
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={onRetry}
            disabled={retryDisabled}
          >
            <ArrowClockwise weight="bold" />
            {t('aiBar.error.retry')}
          </Button>
        </div>
      ) : (
        <>
          {entry.reply && (
            <div className="flex justify-start">
              <p className="max-w-[90%] whitespace-pre-wrap rounded-2xl rounded-bl-sm bg-muted px-3 py-2 text-sm text-foreground">
                {entry.reply}
              </p>
            </div>
          )}
          {entry.proposedIntents.length > 0 && (
            <ul className="mt-3 space-y-3">
              {entry.proposedIntents.map((intent) => (
                <li key={intent.id}>
                  <ProposedIntent
                    intent={intent}
                    slug={slug}
                    token={token}
                    onResolved={(next) => onIntentResolved(intent.id, next)}
                    onRejected={() => onIntentRejected(intent.id)}
                  />
                </li>
              ))}
            </ul>
          )}
          {entry.navigation && (
            <Button
              type="button"
              size="sm"
              className="mt-3"
              onClick={() => onNavigate(entry.navigation!)}
            >
              <ArrowRight weight="bold" />
              {t('aiBar.takeMeThere')}
            </Button>
          )}
        </>
      )}
    </div>
  );
}

interface ProposedIntentProps {
  intent: CommandIntent;
  slug: string;
  token: string | null;
  onResolved: (next: CommandIntent) => void;
  onRejected: () => void;
}

function ProposedIntent({ intent, slug, token, onResolved, onRejected }: ProposedIntentProps) {
  const { t } = useTranslation('common');

  const approve = useMutation({
    mutationFn: () => approveCommand(token, slug, intent.id),
    onSuccess: (next) => onResolved(next),
  });

  const isPending = intent.status === 'pending';
  const isExecuted = intent.status === 'executed';
  const isRejected = intent.status === 'rejected';
  const isFailed = intent.status === 'failed';

  const titleKey = isExecuted
    ? 'aiBar.proposal.titleApproved'
    : isRejected
      ? 'aiBar.proposal.titleRejected'
      : isFailed
        ? 'aiBar.proposal.titleFailed'
        : 'aiBar.proposal.title';

  return (
    <div className="rounded-2xl border border-border bg-muted/30 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t(titleKey)}
      </p>
      <p className="mt-1 text-sm font-semibold text-foreground">{intent.plan_summary}</p>
      {intent.plan_impact && (
        <p className="mt-1 text-sm text-muted-foreground">{intent.plan_impact}</p>
      )}

      <OperationsBlock operations={intent.operations} />

      {isPending && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={() => approve.mutate()}
            disabled={approve.isPending}
          >
            {approve.isPending ? t('aiBar.proposal.approving') : t('aiBar.proposal.approve')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onRejected}
            disabled={approve.isPending}
          >
            {t('aiBar.proposal.reject')}
          </Button>
          <span className="text-xs text-muted-foreground">{t('aiBar.proposal.expiresHint')}</span>
        </div>
      )}
      {isExecuted && (
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-[var(--lime,#BEE034)]/20 px-3 py-2">
          <CheckCircle
            weight="fill"
            className="mt-0.5 h-4 w-4 shrink-0 text-foreground"
            aria-hidden
          />
          <p className="text-xs font-medium text-foreground">{t('aiBar.proposal.approved')}</p>
        </div>
      )}
      {isRejected && (
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-muted px-3 py-2">
          <X
            weight="bold"
            className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden
          />
          <p className="text-xs text-muted-foreground">{t('aiBar.proposal.rejectedNote')}</p>
        </div>
      )}
      {isFailed && (
        <div className="mt-3 flex items-start gap-2 rounded-xl bg-destructive/10 px-3 py-2">
          <X weight="bold" className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
          <p className="text-xs text-destructive">
            {t('aiBar.proposal.failed', { defaultValue: intent.failed_reason ?? '' })}
          </p>
        </div>
      )}
      {approve.isError && (
        <p className="mt-2 text-xs text-destructive">{t('aiBar.proposal.approveError')}</p>
      )}
    </div>
  );
}

// OperationsBlock renders the proposed-intent's operations. When the
// batch fits a "schedule-style" shape — day / time / title repeated
// across many ops (template_slot.create, activity.create, etc.) — it
// renders a compact 3-column table so a 12-pass weekly schedule is
// scannable. Single ops (member.update, team.set_member_assignments,
// location.create, …) get NO inline rendering: the plan_summary and
// plan_impact carry the user-facing description and dumping op_type +
// raw args next to them just confuses non-technical users with internal
// field names.
function OperationsBlock({ operations }: { operations: OperationEnvelope[] }) {
  const { t, i18n } = useTranslation('common');
  const slotRows = extractScheduleRows(operations);
  if (slotRows.length < 3) return null;

  const dayFmt = (idx: number) => {
    const ref = new Date(Date.UTC(2026, 4, 4)); // 2026-05-04 is a Monday
    ref.setUTCDate(ref.getUTCDate() + (idx - 1));
    const txt = new Intl.DateTimeFormat(i18n.resolvedLanguage ?? 'en', { weekday: 'short' }).format(ref);
    return txt.charAt(0).toUpperCase() + txt.slice(1);
  };
  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-border bg-card">
      <table className="w-full text-xs">
        <thead className="bg-muted/40 text-muted-foreground">
          <tr>
            <th className="px-3 py-1.5 text-left font-semibold">{t('aiBar.proposal.col.day')}</th>
            <th className="px-3 py-1.5 text-left font-semibold">{t('aiBar.proposal.col.time')}</th>
            <th className="px-3 py-1.5 text-left font-semibold">{t('aiBar.proposal.col.title')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {slotRows.map((r, i) => (
            <tr key={i}>
              <td className="px-3 py-1.5 text-foreground">{dayFmt(r.day)}</td>
              <td className="px-3 py-1.5 text-foreground">
                {[r.start, r.end].filter(Boolean).join(' – ')}
              </td>
              <td className="px-3 py-1.5 text-foreground">{r.title}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface ScheduleRow {
  day: number;
  start: string;
  end: string;
  title: string;
}
function extractScheduleRows(ops: OperationEnvelope[]): ScheduleRow[] {
  const out: ScheduleRow[] = [];
  for (const op of ops) {
    const a = op.args ?? {};
    if (op.type === 'template_slot.create') {
      const day = typeof a['day_of_week'] === 'number' ? a['day_of_week'] : null;
      const start = typeof a['start_time'] === 'string' ? a['start_time'] : '';
      const end = typeof a['end_time'] === 'string' ? a['end_time'] : '';
      const title = typeof a['title'] === 'string' ? a['title'] : '';
      if (day !== null) {
        out.push({ day, start, end, title });
        continue;
      }
    }
    if (op.type === 'template.create_with_slots') {
      // Phase 4 composite from propose_team_schedule: slots inline in args.
      const rawSlots = Array.isArray(a['slots']) ? (a['slots'] as unknown[]) : [];
      for (const raw of rawSlots) {
        if (!raw || typeof raw !== 'object') continue;
        const s = raw as Record<string, unknown>;
        const day = typeof s['day_of_week'] === 'number' ? (s['day_of_week'] as number) : null;
        if (day === null) continue;
        const start = typeof s['start_time'] === 'string' ? (s['start_time'] as string) : '';
        const end = typeof s['end_time'] === 'string' ? (s['end_time'] as string) : '';
        const title = typeof s['title'] === 'string' ? (s['title'] as string) : '';
        out.push({ day, start, end, title });
      }
      continue;
    }
    if (op.type === 'activity.create') {
      const startsRaw = typeof a['starts_at'] === 'string' ? a['starts_at'] : '';
      const endsRaw = typeof a['ends_at'] === 'string' ? a['ends_at'] : '';
      const title = typeof a['title'] === 'string' ? a['title'] : '';
      if (startsRaw) {
        const d = new Date(startsRaw);
        const isoDay = d.getDay() === 0 ? 7 : d.getDay();
        out.push({ day: isoDay, start: hhmm(startsRaw), end: hhmm(endsRaw), title });
        continue;
      }
    }
    return [];
  }
  return out;
}

function hhmm(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return [String(d.getHours()).padStart(2, '0'), String(d.getMinutes()).padStart(2, '0')].join(':');
}
